/**
 * Boot the campaign GTM container, exactly once, in the right order.
 *
 * WHY THIS IS NOT AN EFFECT IN A COMPONENT. The obvious fix for "/lp/* pushes
 * events nobody drains" is to copy `CampaignShell`'s
 * `useEffect(() => { loadGtm(); applyStoredConsent(); }, [])` into the landing
 * route. That introduces a Consent Mode violation, because React runs CHILD
 * effects before PARENT effects. `LandingPageV3` fires `landing_view` in its
 * own mount effect, so `dataLayer` would read:
 *
 *   1. { event: 'landing_view' }        ← child effect
 *   2. ['consent','default',{denied}]   ← parent effect, loadGtm()
 *   3. ['consent','update', …]          ← applyStoredConsent()
 *
 * GTM replays `dataLayer` in array order, so a default that arrives after an
 * event is a default that never applied to that event.
 *
 * Calling this from `createTracker()` instead puts it in a `useMemo`, which
 * runs during render and therefore strictly before any effect anywhere in the
 * tree. The ordering then cannot be broken by a future page, because the boot
 * travels with the tracker rather than with a component someone might forget.
 *
 * WHY NOT IN THE ROUTE. `LandingRoute` renders `null` and redirects to
 * alchemiq.ai for an unknown slug. Booting there would inject a container and
 * set cookies for someone who mistyped an ad URL and is about to be bounced off
 * the host entirely.
 *
 * The authenticated product stays clean: `gtm.js` only injects on an explicit
 * call, and after this change the only callers are the campaign shells and this
 * module. `campaignTag.test.js` asserts that.
 */

import { loadGtm } from './gtm';
import { applyStoredConsent } from './consent';

let booted = false;

/**
 * Idempotent. `loadGtm` already latches internally, but `applyStoredConsent`
 * does not, and re-pushing a consent update on every tracker construction
 * would be noise in the array GTM replays.
 */
export function ensureCampaignTag() {
  if (booted) return;
  booted = true;
  loadGtm();
  // Replays a stored choice into Consent Mode. Without it, someone who
  // accepted last week is treated as denied on every later visit.
  applyStoredConsent();
}

/** Tests only. */
export function __resetCampaignTagForTests() {
  booted = false;
}
